import React, { useState } from 'react';
import { ValidationError } from 'yup';
import { useTranslation } from 'react-i18next';
import { Alert, AlertType } from 'components/lds';
import { Button, Flex } from 'design-system-web';
import { Channel } from 'lane-shared/types/ChannelType';
import { ExternalReference } from 'graphql-query-contracts';
import { EditExternalReferenceItem } from 'components/externalReferences';
import { useEditPropertyExternalReferences } from '../hooks/useEditPropertyExternalReferences';

type Props = {
  references: ExternalReference[];
  channel: Channel;
  onClose: () => void;
};

export const EditPropertyExternalReferences = ({
  references,
  channel,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { referenceItems, edit, remove, save } =
    useEditPropertyExternalReferences({ references, channel });

  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setErrorMessage('');

    try {
      setIsSaving(true);

      await save();
      window.Toast.show(
        t(
          'web.admin.channel.profile.info.editPropertyExternalReferences.save.success'
        )
      );

      setIsSaving(false);
      onClose();
    } catch (error) {
      setIsSaving(false);

      if (!(error instanceof ValidationError)) {
        setErrorMessage(
          t(
            'web.admin.channel.profile.info.editPropertyExternalReferences.save.error'
          )
        );
      }
    }
  };

  return (
    <Flex direction="column" gap={4}>
      {errorMessage && <Alert type={AlertType.error}>{errorMessage}</Alert>}
      <Flex direction="column" gap={5}>
        {referenceItems.map(reference => (
          <EditExternalReferenceItem
            key={reference.id}
            reference={reference}
            disabled={isSaving}
            editReference={edit}
            removeReference={remove}
          />
        ))}
      </Flex>
      <Flex gap={2}>
        <Button
          variant="primary"
          size="medium"
          testId="save-external-references"
          loading={isSaving}
          onClick={handleSave}
        >
          {t(
            'web.admin.channel.profile.info.editPropertyExternalReferences.button.save'
          )}
        </Button>
        <Button
          variant="secondary"
          size="medium"
          testId="cancel-external-references"
          disabled={isSaving}
          onClick={onClose}
        >
          {t(
            'web.admin.channel.profile.info.editPropertyExternalReferences.button.cancel'
          )}
        </Button>
      </Flex>
    </Flex>
  );
};
